"use client";

import { useRef } from "react";
import { SwiperSlide, Swiper } from "swiper/react";
import { Swiper as SwiperType, Navigation, Pagination } from "swiper";
import cn from "classnames";

import { translate } from "@ascendnow/shared";

import AscProgressBar from "../AscProgressBar/AscProgressBar";
import AscHeading from "../AscTypography/AscHeading/AscHeading";
import AscTooltip from "../AscTooltip/AscTooltip";
import {
  ISkill,
  AscSkillsInfoBlockListProps,
  AscSkillsInfoBlockProps,
} from "./AscSkillsInfoBlock.type";
import styles from "./AscSkillsInfoBlock.module.scss";

export function SkillBlock({ label, value }: ISkill) {
  return (
    <div className={styles.skill}>
      <AscTooltip
        tooltipAnchor={label}
        tooltipAnchorClassName={styles.skillTitle}
      >
        {label?.length > 20 ? label : ""}
      </AscTooltip>
      <AscProgressBar
        type="skills"
        max={100}
        value={value ? Number(value.toFixed(2)) : 0}
        className={styles.skillProgressBar}
      />
    </div>
  );
}

export function AscSkillsInfoBlockList({
  categories,
  className,
  onNextClick,
}: AscSkillsInfoBlockListProps) {
  const swiperRef = useRef<SwiperType>();
  const navigationPrevRef = useRef<HTMLDivElement>(null);
  const navigationNextRef = useRef<HTMLDivElement>(null);
  return (
    <Swiper
      slidesPerView={1}
      modules={[Navigation, Pagination]}
      onBeforeInit={(swiper) => {
        swiperRef.current = swiper;
      }}
      navigation={{
        prevEl: navigationPrevRef.current,
        nextEl: navigationNextRef.current,
      }}
      pagination={{
        clickable: true,
        el: "#swiper-pagination",
      }}
      className={cn(styles.skillGroup, className)}
    >
      {categories &&
        categories.length > 0 &&
        categories.map((category, index) => (
          <SwiperSlide key={index} style={{ width: "auto" }}>
            <AscSkillsInfoBlock {...{ ...category }} />
          </SwiperSlide>
        ))}
      <div className={styles.swiperBottom}>
        <div ref={navigationPrevRef} className={styles.swiperPrev} />
        <div id="swiper-pagination" className={styles.swiperPagination} />
        <div
          ref={navigationNextRef}
          className={styles.swiperNext}
          onClick={onNextClick}
        />
      </div>
    </Swiper>
  );
}

export default function AscSkillsInfoBlock({
  className,
  title,
  subtitle,
  data,
}: AscSkillsInfoBlockProps) {
  return (
    <div
      data-testid="asc-skills-info-block"
      className={cn(styles.ground, className)}
    >
      <AscHeading level={6} className={styles.title}>
        {title}
      </AscHeading>
      <span className={styles.subtitle}>{subtitle}</span>

      <div className={styles.skillList}>
        {data?.length > 0 ? (
          data?.map((skill: ISkill, index) => (
            <SkillBlock
              key={skill.label}
              value={skill.value}
              label={skill.label}
            />
          ))
        ) : (
          <div className={styles.noData}>{translate("noData")}</div>
        )}
      </div>
    </div>
  );
}

AscSkillsInfoBlock.List = AscSkillsInfoBlockList;
